'use client';

import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useSearchParams, usePathname, useParams } from 'next/navigation';
import useRedirect from '@src/hooks/useRedirect';
import stringToDash from '@src/helper/stringToDash';
import capitalizeFirstLetter from '@src/helper/capitalizeFirstLetter';

// Apis
import { useQueryPropertyCategories } from '@src/apis/shared/get-property-categories';
import useGlobalCtx from '@src/context/global-ctx';
import { useGetListProperties } from '@src/apis/list-listing/query';
import handleError from '@src/helper/handleError';

const ListListingCtx = createContext();

const LIMIT = 15;

const initForm = {
  'price[min]': null,
  'price[max]': null,
  bedroom: null,
  additional_bedroom: null,
  bathroom: null,
  additional_bathroom: null,
  'surface_area[min]': null,
  'surface_area[max]': null,
  'building_area[min]': null,
  'building_area[max]': null,
};
export const ListListingProvider = ({
  pathType, // type, keywords, city, area, category, city-category, city-area-category
  mappedData,
  children,
}) => {
  const { setShowLoader } = useGlobalCtx();
  const redirect = useRedirect();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  
  const searchOrder = searchParams.get('_order');
  const { city, area, category } = useParams();

  const isCategoryFirstPath = pathType === 'category';
  const isCategorySecondPath = pathType === 'city-category';
  const isCategoryThirdPath = pathType === 'city-area-category';
  const isCity = pathType === 'city';
  const isArea = pathType === 'area';
  const isKeywords = pathType === 'keywords';

  const categorySlug = useMemo(() => {
    if (isCategoryFirstPath) {
      return capitalizeFirstLetter(city?.split('-').join(' ') || '');
    }
    if (isCategorySecondPath) {
      return capitalizeFirstLetter(area?.split('-').join(' ') || '');
    }
    if (isCategoryThirdPath) {
      return capitalizeFirstLetter(category?.split('-').join(' ') || '');
    }
    return null;
  }, [
    isCategoryFirstPath,
    isCategorySecondPath,
    isCategoryThirdPath,
    city,
    area,
    category,
  ]);
  const { data: listPropertyCategories } = useQueryPropertyCategories();

  const findCategoryId = useMemo(() => {
    return listPropertyCategories?.find((category) => category.name.replace('/ ', '').toLowerCase() === categorySlug?.toLowerCase())?.id;
  }, [listPropertyCategories, categorySlug]);

  const propertyCategories = findCategoryId || searchParams.get('property_category_id');

  const areaSlug = useMemo(() => {
    return (isArea || isCategoryThirdPath) ? capitalizeFirstLetter(area?.split('-').join(' ') || '') : '';
  }, [isArea, isCategoryThirdPath, area]);

  const citySlug = useMemo(() => {
    return (isCity || isCategorySecondPath || isArea || isCategoryThirdPath) ? capitalizeFirstLetter(city?.split('-').join(' ') || '') : '';
  }, [isCity, isCategorySecondPath, isArea, isCategoryThirdPath, city]);

  const cityName = useMemo(() => {
    if (isCategoryFirstPath) {
      return '';
    }

    if (isArea || isCategoryThirdPath) {
      return `${areaSlug}, ${citySlug}`;
    }

    if (isCity || isCategorySecondPath) {
      return citySlug;
    }

    return searchParams.get('cityName');
  }, [
    isArea,
    isCity,
    areaSlug,
    citySlug,
    searchParams,
    isCategoryFirstPath,
    isCategorySecondPath,
    isCategoryThirdPath,
  ]);

  const keyword = searchParams.get('q') || searchParams.get('keyword');
  // const cityId = searchParams.get('city_id');
  // const areaId = searchParams.get('area_ids');

  const stringCity = city?.replace(/-/g, ' ');
  const stringArea = area?.replace(/-/g, ' ');

  const [propertyCategory, setPropertyCategory] = useState(null);
  const [searchDisplay, setSearchDisplay] = useState(
    !!cityName ? cityName : keyword ? keyword : ''
  ); // this use for search

  const [filter, setFilter] = useState(() => {
    const newFilter = { ...initForm };

    for (let key in newFilter) {
      const value = searchParams.get(key);

      if (!!value) {
        newFilter[key] = +value;
      }
    }

    return newFilter;
  });

  const [params, setParams] = useState(() => {
    const { type } = mappedData ?? {};

    const result = {
      _limit: LIMIT,
      _order: null,
      page: 1,
      property_category_id: null,
      property_type_id: 1,
      // area_ids: null,
      // city_id: null,
      area_name: null,
      city_name: null,
      keyword: null,
      ...filter,
    };

    if (isArea || isCategoryThirdPath) {
      // result.area_ids = !!areaId ? `${areaId}` : null;
      // result.city_id = null;
      result.area_name = !!stringArea ? stringArea : null;
      result.city_name = null;
    } else if (isCity || isCategorySecondPath) {
      // result.area_ids = null;
      // result.city_id = !!cityId ? `${cityId}` : null;
      result.area_name = null;
      result.city_name = !!stringCity ? stringCity : null;
    } else if (isKeywords) {
      result.area_name = null;
      result.city_name = null;
      result.keyword = !!keyword ? `${keyword}` : null;
    }

    if (!!type?.id) {
      result.property_type_id = type.id;
    }

    if (!!searchOrder) {
      result._order = searchOrder;
    }
    
    if (!!propertyCategories) {
      result.property_category_id = propertyCategories;
    }

    return result;
  });


  const {
    data: listListing,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    error,
  } = useGetListProperties({ params });

  const handleNextPage = () => {
    let newParams = {
      ...params,
      page: params.page + 1,
    };
    setParams(newParams);
  };

  const handleRedirect = (type, value) => {
    let splittedUrl = pathname.split('/');

    let query = '';

    for (let key in params) {
      const value = params[key];
      const condition =
        key === 'property_type_id' ||
        key === 'area_name' ||
        key === 'city_name' ||
        key === '_limit' ||
        key === 'page';

      if (type === 'search' && key === 'keyword') {
        continue;
      }

      if (condition) {
        continue;
      }

      if (!!value) {
        if (!!query) {
          query += `&${key}=${value}`;
        } else {
          query += `?${key}=${value}`;
        }
      }
    }

    if (type === 'property-type') {
      let __value = value === 1 ? 'jual' : 'sewa';
      splittedUrl[1] = __value;

      // if (areaId) {
      //   query += !!query ? `&area_ids=${areaId}` : `?area_ids=${areaId}`;
      // } else if (cityId) {
      //   query += !!query ? `&city_id=${cityId}` : `?city_id=${cityId}`;
      // } else if (keyword) {
      //   query += !!query ? `&q=${keyword}` : `?q=${keyword}`;
      // }

      if (keyword) {
        query += !!query ? `&q=${keyword}` : `?q=${keyword}`;
      }
    } else if (type === 'search') {
      const splittedLocation = !!value?.name ? value.name.split(', ') : [];

      if (!value) {
        splittedUrl[1] = undefined;
        splittedUrl[2] = undefined;
        splittedUrl[3] = undefined;
      } else if (value?.slug === 'Area') {
        let area = stringToDash(splittedLocation[0]);
        let city = stringToDash(splittedLocation[1]);

        splittedUrl[2] = city;
        splittedUrl[3] = area;

        // query += !!query ? `&area_ids=${value.id}` : `?area_ids=${value.id}`;
      } else if (value?.slug === 'City') {
        let city = stringToDash(splittedLocation[0]);
        splittedUrl[2] = city;
        splittedUrl[3] = undefined;
        // query += !!query ? `&city_id=${value.id}` : `?city_id=${value.id}`;
      } else if (value?.slug === 'Keyword' && pathType !== 'keywords') {
        splittedUrl[2] = 'keywords';
        splittedUrl[3] = undefined;
        query += !!query ? `&q=${value.name}` : `?q=${value.name}`;
      } else {
        splittedUrl[2] = undefined;
        splittedUrl[3] = undefined;
      }
    }

    if (!['jual', 'sewa'].includes(splittedUrl[1])) {
      splittedUrl[1] = 'jual';
    }

    let url = splittedUrl.join('/');

    setShowLoader(true);
    redirect(`${url}${query}`);
    setTimeout(() => {
      setShowLoader(false);
    }, 5000);
  };

  const handleClearKeyword = () => {
    let splittedUrl = pathname.split('/');

    let query = '';

    for (let key in params) {
      const value = params[key];

      if (key === 'keyword') {
        continue;
      }

      if (!!value) {
        if (!!query) {
          query += `&${key}=${value}`;
        } else {
          query += `?${key}=${value}`;
        }
      }
    }

    splittedUrl[2] = undefined;
    splittedUrl[3] = undefined;

    let url = splittedUrl.join('/');

    setShowLoader(true);
    redirect(`${url}${query}`);
    setTimeout(() => {
      setShowLoader(false);
    }, 5000);
  };

  const handleApply = (type, value, callback) => {
    let newParams = {
      ...params,
      page: 1,
    };

    switch (type) {
      case 'filter': {
        newParams = {
          ...newParams,
          ...value,
        };
        newParams.property_category_id = !!value?.property_category_id
          ? `${value.property_category_id}`
          : null;
        break;
      }
      case 'order':
      default: {
        newParams._order = value;
        break;
      }
    }

    const isSearch = type === 'search';
    const isPathPropertyType = type === 'property-type';
    const isKeywordSlug = value?.slug === 'Keyword';
    const isPathKeyword = pathType === 'keywords';

    if (isSearch && isKeywordSlug && isPathKeyword) {
      newParams = { ...params, keyword: value.name };
    } else if (isSearch && isPathKeyword && !value) {
      return handleClearKeyword();
    } else if (isPathPropertyType || isSearch) {
      return handleRedirect(type, value);
    }

    setParams(newParams);
    typeof callback === 'function' && callback();
  };

  useEffect(() => {
    if (isError) {
      const err = handleError(error);
      toast.error(`status: ${err?.code}, message: ${err?.message}`);
    }
  }, [isError, error]);

  useEffect(() => {
    setShowLoader(false);
  }, []);

  return (
    <ListListingCtx.Provider
      value={{
        isLoading,
        hasNextPage,
        handleNextPage,
        propertyCategory,
        setPropertyCategory,
        listPropertyCategories,
        initForm,
        filter,
        setFilter,
        handleApply,
        params,
        setParams,
        listListing,
        isFetchingNextPage,
        fetchNextPage,
        searchDisplay,
        setSearchDisplay,
      }}
    >
      {children}
    </ListListingCtx.Provider>
  );
};

const useListListingCtx = () => {
  const ctx = useContext(ListListingCtx);

  if (!ctx) {
    throw new Error(
      'useListListingCtx must be used within the ListListingProvider'
    );
  }

  return ctx;
};

export default useListListingCtx;
